export const meta = {
  "title": "FHIRfly Now Supports UK Health Terminology",
  "slug": "uk-health-terminology-dmd-opcs4",
  "excerpt": "FHIRfly adds dm+d and OPCS-4 APIs — bringing NHS medicines and procedure classifications to the same platform as NDC, RxNorm, and ICD-10.",
  "publishedAt": "2026-03-19T15:32:53.326Z",
  "tags": [
    "fhir",
    "uk-health",
    "dmd",
    "opcs4",
    "interoperability"
  ],
  "voice": "marketing",
  "author": "FHIRfly",
  "seoTitle": "FHIRfly Now Supports UK Health Terminology | FHIRfly Blog",
  "seoDescription": "FHIRfly adds dm+d and OPCS-4 APIs — bringing NHS medicines and procedure classifications to the same platform as NDC, RxNorm, and ICD-10."
};

![UK and US clinicians and developers connected through FHIRfly health terminology APIs](/blog/heroes/uk-health-terminology-dmd-opcs4.png)

If you're building healthcare applications that cross the Atlantic, you've felt the pain: US terminology on one side, NHS terminology on the other, and no single API that covers both. That changes today.

FHIRfly now supports two foundational NHS data sets — **dm+d** (Dictionary of Medicines and Devices) and **OPCS-4** (Office of Population Censuses and Surveys Classification of Interventions and Procedures) — alongside our existing US terminology coverage.

## Why This Matters

Healthcare is increasingly global. Pharma companies run clinical trials across the US and UK. Digital health startups serve patients in both markets. Research teams need to map between coding systems daily.

Until now, working with UK health terminology meant either licensing expensive enterprise platforms or parsing raw NHS TRUD XML files yourself. Neither option is developer-friendly.

With dm+d and OPCS-4 on FHIRfly, you get the same clean REST API experience you already have for NDC, RxNorm, ICD-10, and LOINC — just pointed at UK data.

## dm+d: The NHS Drug Dictionary

The [Dictionary of Medicines and Devices](https://www.nhsbsa.nhs.uk/pharmacies-gp-practices-and-appliance-contractors/dictionary-medicines-and-devices-dmd) is the NHS standard for identifying medicines and medical devices. It covers approximately **415,000 concepts** organized in a five-level hierarchy:

| Level | Type | Example |
|-------|------|--------|
| **VTM** | Virtual Therapeutic Moiety | Paracetamol |
| **VMP** | Virtual Medicinal Product | Paracetamol 500mg tablets |
| **AMP** | Actual Medicinal Product | Paracetamol 500mg tablets (Boots) |
| **VMPP** | Virtual Medicinal Product Pack | Paracetamol 500mg tablets x 16 |
| **AMPP** | Actual Medicinal Product Pack | Paracetamol 500mg tablets x 16 (Boots) |

This hierarchy lets you work at whatever level of specificity you need — from active ingredient down to the exact branded pack on a pharmacy shelf.

### API Example

```bash
# Look up a dm+d code
curl -H "x-api-key: YOUR_KEY" \
  "https://api.fhirfly.io/v1/dmd/39113611000001102"
```

```json
{
  "code": "39113611000001102",
  "display": "Paracetamol 500mg tablets",
  "concept_type": "VMP",
  "status": "active",
  "fhir_coding": {
    "system": "https://dmd.nhs.uk",
    "code": "39113611000001102",
    "display": "Paracetamol 500mg tablets"
  }
}
```

Batch lookups (up to 100 codes) and full-text search are also available:

```bash
# Search across all concept types
curl -H "x-api-key: YOUR_KEY" \
  "https://api.fhirfly.io/v1/dmd/search?q=amoxicillin&concept_type=VMP"
```

## OPCS-4: NHS Procedure Codes

[OPCS-4](https://icd.who.int/dev11/l-opcs/en) is the procedure classification used across NHS England for clinical coding, hospital episode statistics, and reimbursement. Think of it as the UK equivalent of CPT or ICD-10-PCS.

The current version (4.11) contains approximately **1,665 codes** organized across 22 chapters, from nervous system procedures (Chapter A) through to subsidiary classifications (Chapter Z).

### API Example

```bash
# Look up a procedure code
curl -H "x-api-key: YOUR_KEY" \
  "https://api.fhirfly.io/v1/opcs4/W37.1"
```

```json
{
  "code": "W37.1",
  "display": "Primary total prosthetic replacement of hip joint using cement",
  "chapter": "W",
  "chapter_description": "Other bones and joints",
  "fhir_coding": {
    "system": "https://fhir.nhs.uk/CodeSystem/OPCS-4",
    "code": "W37.1",
    "display": "Primary total prosthetic replacement of hip joint using cement"
  }
}
```

## FHIR-Native From Day One

Both dm+d and OPCS-4 responses include `fhir_coding` fields with the correct system URIs, so you can drop them straight into FHIR resources without manual mapping. This is consistent with how we handle US terminologies — NDC, RxNorm, LOINC, ICD-10, CVX, and SNOMED all include their respective FHIR system URIs.

If you're building FHIR R4 resources that need to work in both US and UK contexts, having a single API that returns properly-coded data for both jurisdictions saves significant integration effort.

## Data Freshness and Licensing

Both data sets are sourced directly from [NHS TRUD](https://isd.digital.nhs.uk/trud/) (Technology Reference data Update Distribution) and updated automatically:

| Dataset | Update Frequency | Source | License |
|---------|-----------------|--------|--------|
| dm+d | Weekly | NHS TRUD Item 24 | Open Government Licence v3.0 |
| OPCS-4 | Periodic (on NHS release) | NHS TRUD Item 119 | Open Government Licence v3.0 |

Both are published under the UK Open Government Licence, which permits commercial use with attribution — the same permissive licensing model as our US data sources.

## What's on the Platform Now

With dm+d and OPCS-4, FHIRfly now covers **17 clinical terminology data types** across both US and UK jurisdictions:

**US Coverage:** NDC, NPI, RxNorm, LOINC, ICD-10-CM, ICD-10-PCS, CVX, MVX, FDA Drug Labels, SNOMED CT, HCC Crosswalks, NCCI PTP, MUE, PFS/RVU, LCD/NCD Coverage

**UK Coverage:** dm+d, OPCS-4

**Cross-jurisdiction:** SNOMED CT (international)

Every data type supports single lookup, batch operations (up to 100 codes), and full-text search through a consistent API interface.

## Key Takeaways

- **dm+d and OPCS-4 are live** on the FHIRfly API — same endpoints, same authentication, same response patterns as US terminologies.
- **FHIR-native responses** include correct system URIs for both UK and US coding systems, ready for FHIR R4 resources.
- **Weekly updates from NHS TRUD** keep dm+d current with the latest NHS formulary changes.
- **Open licensing** (OGL v3.0) means no additional licensing fees for UK data — it's included in your FHIRfly plan.

## Further Reading

- [dm+d API Documentation](/docs/value-apis/dmd) — endpoints, response shapes, and code examples
- [OPCS-4 API Documentation](/docs/value-apis/opcs4) — procedure code lookups and search
- [dm+d Search Guide](/docs/search-apis/dmd) — filtering by concept type, status, and more
- [Data Sources & Licensing](/docs/legal/data-sources) — full attribution and licensing details for all 17 data types
